import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileEdit, Loader2, RotateCcw, Send,
    Plus, Trash2, Table2, FileStack, CheckCircle, Navigation, Paperclip,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { inputCls, Label, SelectIcon, SectionHeader } from '../../components/vendor/sharedVendorUI';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS, buildSPPOAmendUrl } from '../../config/s3Config';

import {
    fetchVendorsForSPPOAmend, fetchCCForSPPOAmend, fetchPOForSPPOAmend, fetchSPPOByNoForAmend,
    fetchUploadDocSPPOAmendValue, fetchSPPOAmendUploadedDocs,
    checkSPPOAmendDescriptions, saveNewSPPOAmend,
    clearCostCenters, clearPONumbers, clearPODetails, clearUploadedDocs,
    clearCheckResult, clearSaveResult, resetSPPOAmendForm, resetSPPOAmend,
    selectVendorsArray, selectVendorsLoading,
    selectCostCentersArray, selectCostCentersLoading,
    selectPONumbersArray, selectPONumbersLoading,
    selectPODetails, selectPODetailsLoading,
    selectUploadValue,
    selectUploadedDocsArray, selectUploadedDocsLoading,
    selectSaveStatus, selectSaveLoading, selectSaveError,
} from '../../slices/spPOSlice/sppoAmendCreationSlice';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const parseAPIDate = (val) => {
    if (!val) return null;
    const m = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(val);
    if (m) {
        const [, dd, mon, yyyy] = m;
        const idx = MONTH_ABBR.findIndex((x) => x.toLowerCase() === mon.toLowerCase());
        if (idx >= 0) return new Date(parseInt(yyyy, 10), idx, parseInt(dd, 10));
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

const numOr0 = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

const newOldRow = (item) => ({
    id: item.SPPOItemId,
    description: item.Description,
    unit: item.Unit,
    quantity: item.Quantity,
    rate: item.Rate,
    checked: false,
    type: '',
    amendQty: '',
});

const newItemRow = () => ({
    id: Date.now() + Math.random(),
    description: '', unit: '', quantity: '', ourRate: '', prwRate: '', rate: '',
    checked: true,
});

const newTermRow = () => ({ id: Date.now() + Math.random(), term: '' });

const SPPOAmend = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    const vendors = useSelector(selectVendorsArray);
    const vendorsLoading = useSelector(selectVendorsLoading);
    const costCenters = useSelector(selectCostCentersArray);
    const ccLoading = useSelector(selectCostCentersLoading);
    const poNumbers = useSelector(selectPONumbersArray);
    const poNumbersLoading = useSelector(selectPONumbersLoading);
    const poDetails = useSelector(selectPODetails);
    const poDetailsLoading = useSelector(selectPODetailsLoading);
    const uploadValue = useSelector(selectUploadValue);
    const uploadedDocs = useSelector(selectUploadedDocsArray);
    const uploadedDocsLoading = useSelector(selectUploadedDocsLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    const [vendorCode, setVendorCode] = useState('');
    const [ccCode, setCcCode] = useState('');
    const [sppoNo, setSppoNo] = useState('');

    const [amendDate, setAmendDate] = useState(null);
    const [oldItems, setOldItems] = useState([]);
    const [oldTerms, setOldTerms] = useState([]);
    const [showNewItems, setShowNewItems] = useState(false);
    const [newItems, setNewItems] = useState([newItemRow()]);
    const [newTerms, setNewTerms] = useState([newTermRow()]);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [checking, setChecking] = useState(false);

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchVendorsForSPPOAmend());
        return () => { dispatch(resetSPPOAmend()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        setCcCode(''); setSppoNo('');
        dispatch(clearCostCenters()); dispatch(clearPONumbers()); dispatch(clearPODetails());
        if (vendorCode) dispatch(fetchCCForSPPOAmend(vendorCode));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorCode]);

    useEffect(() => {
        setSppoNo('');
        dispatch(clearPONumbers()); dispatch(clearPODetails());
        if (vendorCode && ccCode) dispatch(fetchPOForSPPOAmend({ vendorCode, ccCode }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccCode]);

    const resetPOSection = () => {
        setAmendDate(null); setOldItems([]); setOldTerms([]);
        setShowNewItems(false); setNewItems([newItemRow()]); setNewTerms([newTermRow()]);
        setFile(null);
        dispatch(clearPODetails());
        dispatch(clearUploadedDocs());
    };

    useEffect(() => {
        resetPOSection();
        if (vendorCode && ccCode && sppoNo) {
            dispatch(fetchSPPOByNoForAmend({ sppono: sppoNo, ccCode, vendorCode }));
            dispatch(fetchSPPOAmendUploadedDocs({ poNo: sppoNo, forType: 'SPPO' }));
            dispatch(fetchUploadDocSPPOAmendValue(ccCode));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sppoNo]);

    useEffect(() => {
        if (!poDetails) return;
        setAmendDate(parseAPIDate(poDetails.SPPOStartDate));
        setOldItems((poDetails.ItemDescList || []).map(newOldRow));
        const terms = (poDetails.Remarks || '').split('|').map((t) => t.trim()).filter(Boolean);
        setOldTerms(terms.map((t) => ({ id: Math.random(), term: t })));
    }, [poDetails]);

    useEffect(() => {
        if (saveStatus === 'failed' && saveError) { toast.error(saveError); dispatch(clearSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Old items handlers ────────────────────────────────────────────────
    const updateOldItem = (id, patch) => {
        setOldItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    };

    const handleOldCheck = (id, checked) => {
        updateOldItem(id, checked ? { checked: true } : { checked: false, type: '', amendQty: '' });
    };

    const handleOldTypeChange = (id, type) => {
        updateOldItem(id, { type, amendQty: '' });
    };

    const handleOldQtyChange = (row, val) => {
        if (val !== '' && !/^\d*\.?\d{0,2}$/.test(val)) return;
        if (!row.type) { toast.warn('Select Type Before Adding Quantity.'); updateOldItem(row.id, { amendQty: '' }); return; }
        if (row.type === 'Substract' && val !== '' && numOr0(val) > numOr0(row.quantity)) {
            toast.warn('Invalid Substraction Quantity.');
            updateOldItem(row.id, { amendQty: '' });
            return;
        }
        updateOldItem(row.id, { amendQty: val });
    };

    const oldItemAmount = (row) => (row.amendQty ? numOr0(row.rate) * numOr0(row.amendQty) : 0);

    // ── New items handlers ────────────────────────────────────────────────
    const updateNewItem = (id, patch) => setNewItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const newItemAmount = (row) => (row.quantity && row.rate ? numOr0(row.quantity) * numOr0(row.rate) : 0);

    const handleAddNewItems = () => setShowNewItems(true);
    const handleRemoveNewItems = () => { setShowNewItems(false); setNewItems([newItemRow()]); };

    // ── Terms handlers ────────────────────────────────────────────────────
    const updateNewTerm = (id, term) => setNewTerms((prev) => prev.map((r) => (r.id === id ? { ...r, term } : r)));

    // ── Totals ────────────────────────────────────────────────────────────
    const totals = useMemo(() => {
        let plus = 0, minus = 0;
        oldItems.forEach((r) => {
            if (!r.checked || !r.amendQty) return;
            const amt = oldItemAmount(r);
            if (r.type === 'Add') plus += amt;
            if (r.type === 'Substract') minus += amt;
        });
        let newItemsTotal = 0;
        if (showNewItems) {
            newItems.forEach((r) => { if (r.checked) newItemsTotal += newItemAmount(r); });
        }
        const totalPlus = plus + newItemsTotal;
        const poValue = numOr0(poDetails?.TotalValue);
        const total = poValue + totalPlus - minus;
        const netChange = totalPlus - minus;
        return { plus: totalPlus, minus, total, netChange };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oldItems, newItems, showNewItems, poDetails]);

    const requiresUpload = totals.netChange > uploadValue;

    // ── Reset / handlers ──────────────────────────────────────────────────
    const handleFullReset = () => {
        setVendorCode(''); setCcCode(''); setSppoNo('');
        resetPOSection();
        dispatch(resetSPPOAmend());
        dispatch(fetchVendorsForSPPOAmend());
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0] || null;
        if (f && !f.name.toLowerCase().endsWith('.pdf')) { e.target.value = ''; setFile(null); return; }
        setFile(f);
    };

    const handleSubmit = async () => {
        if (!vendorCode) { toast.warn('Select Vendor.'); return; }
        if (!ccCode) { toast.warn('Select Cost Center.'); return; }
        if (!sppoNo) { toast.warn('Select SPPO No.'); return; }
        if (!amendDate) { toast.warn('Select Amend Date.'); return; }

        const checkedOld = oldItems.filter((r) => r.checked);
        for (const r of checkedOld) {
            if (!r.type) { toast.warn('Select a Type for every checked existing item.'); return; }
            if (!r.amendQty || numOr0(r.amendQty) <= 0) { toast.warn('Enter an Amend Quantity for every checked existing item.'); return; }
        }

        const checkedNew = showNewItems ? newItems.filter((r) => r.checked) : [];
        for (const r of checkedNew) {
            if (!r.description.trim()) { toast.warn('Enter Description for every new item.'); return; }
            if (!r.unit.trim()) { toast.warn('Enter Unit for every new item.'); return; }
            if (!r.quantity || numOr0(r.quantity) <= 0) { toast.warn('Enter Quantity for every new item.'); return; }
            if (!r.ourRate || numOr0(r.ourRate) <= 0) { toast.warn('Enter Our Rate for every new item.'); return; }
            if (!r.prwRate || numOr0(r.prwRate) <= 0) { toast.warn('Enter Approved PRW Rate for every new item.'); return; }
            if (!r.rate || numOr0(r.rate) <= 0) { toast.warn('Enter Rate for every new item.'); return; }
            if (numOr0(r.prwRate) < numOr0(r.rate)) { toast.warn('Rate is greater than Approved PRW Rate.'); return; }
        }

        const validNewTerms = newTerms.filter((t) => t.term.trim());
        if (validNewTerms.length === 0) { toast.warn('Enter Atleast One Terms & Conditions.'); return; }

        if (requiresUpload && !file) { toast.warn('Upload the Final Scope Of Checklist document (required for this amount of change).'); return; }

        const oldItemIds = checkedOld.map((r) => r.id).join('|') + (checkedOld.length ? '|' : '');
        const oldItemDescs = checkedOld.map((r) => r.description).join('|') + (checkedOld.length ? '|' : '');
        const oldItemUnits = checkedOld.map((r) => r.unit).join('|') + (checkedOld.length ? '|' : '');
        const oldItemQtys = checkedOld.map((r) => r.amendQty).join('|') + (checkedOld.length ? '|' : '');
        const oldItemRates = checkedOld.map((r) => r.rate).join('|') + (checkedOld.length ? '|' : '');
        const oldItemAmounts = checkedOld.map((r) => oldItemAmount(r).toFixed(2)).join('|') + (checkedOld.length ? '|' : '');
        const oldItemAmendTypes = checkedOld.map((r) => r.type).join('|') + (checkedOld.length ? '|' : '');

        const newItemDescriptions = checkedNew.map((r) => r.description.replace(/\|/g, '')).join('|') + (checkedNew.length ? '|' : '');
        const newItemUnits = checkedNew.map((r) => r.unit).join('|') + (checkedNew.length ? '|' : '');
        const newItemQtys = checkedNew.map((r) => r.quantity).join('|') + (checkedNew.length ? '|' : '');
        const newItemRates = checkedNew.map((r) => r.rate).join('|') + (checkedNew.length ? '|' : '');
        const newItemAmounts = checkedNew.map((r) => newItemAmount(r).toFixed(2)).join('|') + (checkedNew.length ? '|' : '');
        const newItemClientRates = checkedNew.map((r) => r.ourRate).join('|') + (checkedNew.length ? '|' : '');
        const newItemPWRRate = checkedNew.map((r) => r.prwRate).join('|') + (checkedNew.length ? '|' : '');

        const termsJoined = validNewTerms.map((t) => t.term.trim().replace(/\|/g, '')).join('|') + '|';

        const extension = file ? file.name.split('.').pop() : '';

        const basePayload = {
            VendorCode: vendorCode,
            CCCode: ccCode,
            SPPONo: sppoNo,
        };

        setChecking(true);
        try {
            const checkResult = await dispatch(checkSPPOAmendDescriptions({
                ...basePayload,
                SubstractAmount: totals.minus,
                NewItemDescriptions: newItemDescriptions,
            }));
            if (!checkSPPOAmendDescriptions.fulfilled.match(checkResult)) return;
            const checkStr = typeof checkResult.payload?.Data === 'string' ? checkResult.payload.Data : '';
            if (checkStr !== 'NotExists') {
                toast.error(checkStr === 'Exists' ? 'A new item with this description already exists on the PO.' : (checkStr || 'Validation failed.'));
                return;
            }
            dispatch(clearCheckResult());
        } finally {
            setChecking(false);
        }

        const payload = {
            ...basePayload,
            AmendDate: formatDateForAPI(amendDate),
            AmendAmount: totals.plus,
            SubstractAmount: totals.minus,
            OldItemIds: oldItemIds,
            OldItemDescs: oldItemDescs,
            OldItemUnits: oldItemUnits,
            OldItemQtys: oldItemQtys,
            OldItemRates: oldItemRates,
            OldItemAmounts: oldItemAmounts,
            OldItemAmendTypes: oldItemAmendTypes,
            NewItemDescriptions: newItemDescriptions,
            NewItemUnits: newItemUnits,
            NewItemQtys: newItemQtys,
            NewItemRates: newItemRates,
            NewItemAmounts: newItemAmounts,
            NewItemClientRates: newItemClientRates,
            NewItemPWRRate: newItemPWRRate,
            Terms: termsJoined,
            Action: 'Add',
            Filechk: file ? 'Yes' : 'No',
            Extension: extension,
            RoleId: roleId,
            CreatedBy: userName,
        };

        const result = await dispatch(saveNewSPPOAmend(payload));
        if (saveNewSPPOAmend.fulfilled.match(result)) {
            const responseStr = typeof result.payload?.Data === 'string' ? result.payload.Data : '';
            const [status, iCode] = responseStr.split('$');
            if (status === 'Submited') {
                if (file && iCode) {
                    setUploading(true);
                    try { await uploadFileToS3(file, S3_FOLDERS.SPPO_AMENDMENTS, iCode); }
                    catch { toast.warn('Amendment saved, but the document upload failed. Please retry the upload separately.'); }
                    finally { setUploading(false); }
                }
                toast.success('SPPO Amend Details Added Successfully.');
                dispatch(clearSaveResult());
                handleFullReset();
            } else {
                // Legacy shows the SP's message when the status is not 'Submited'.
                toast.error(responseStr || 'Error Occurred While Adding New SPPO Amend');
            }
        }
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || uploading || checking;
    const showMain = !!poDetails;

    const showSummary = showMain && newTerms.some((t) => t.term.trim());

    const canSubmit = showMain && !isBusy;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <FileEdit className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Purchase Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'SPPO Amend'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Amend an existing Service Provider Purchase Order</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Purchase / SPPO Amend</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Selection ──────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader icon={FileEdit} title="Select PO" subtitle="Vendor, cost center and SPPO number"
                        action={
                            <button onClick={handleFullReset} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>Vendor</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={vendorCode} onChange={(e) => setVendorCode(e.target.value)} disabled={vendorsLoading || isBusy}>
                                        <option value="">{vendorsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {vendors.map((v) => <option key={v.VendorCode} value={v.VendorCode}>{v.VendorName}</option>)}
                                    </select>
                                    <SelectIcon loading={vendorsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={ccCode} onChange={(e) => setCcCode(e.target.value)} disabled={!vendorCode || ccLoading || isBusy}>
                                        <option value="">{!vendorCode ? 'Select vendor first' : ccLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {costCenters.map((c) => <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} — {c.CC_Name}</option>)}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>SPPO No</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={sppoNo} onChange={(e) => setSppoNo(e.target.value)} disabled={!ccCode || poNumbersLoading || isBusy}>
                                        <option value="">{!ccCode ? 'Select cost center first' : poNumbersLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {poNumbers.map((p) => <option key={p.SPPONo} value={p.SPPONo}>{p.SPPONoDesc}</option>)}
                                    </select>
                                    <SelectIcon loading={poNumbersLoading} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {poDetailsLoading && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-10 flex items-center justify-center text-gray-400">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading PO details…
                    </div>
                )}

                {showMain && (
                    <>
                        {/* ── Section 2: PO Info ───────────────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={FileEdit} title="PO Information" />
                            <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <Label required>Amend Date</Label>
                                    <CustomDatePicker
                                        value={amendDate} onChange={setAmendDate} disabled={isBusy}
                                        minDate={parseAPIDate(poDetails.SPPOStartDate)} maxDate={parseAPIDate(poDetails.SPPOEndDate)}
                                    />
                                </div>
                                <div>
                                    <Label>PO Value</Label>
                                    <input type="text" readOnly value={`₹ ${formatIndianCurrency(poDetails.TotalValue)}`} className={inputCls} />
                                </div>
                                <div>
                                    <Label>PO Balance</Label>
                                    <input type="text" readOnly value={`₹ ${formatIndianCurrency(poDetails.Balance)}`} className={inputCls} />
                                </div>
                            </div>
                        </div>

                        {/* ── Section 3: Current Items ─────────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={Table2} title="Current Item Description" subtitle="Check a row and choose Add/Substract to amend its quantity" />
                            <div className="p-6 md:p-8">
                                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                                                <th className="px-3 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Chk</th>
                                                <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Description</th>
                                                <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Unit</th>
                                                <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Quantity</th>
                                                <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Rate</th>
                                                <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Type</th>
                                                <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Amend Qty</th>
                                                <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {oldItems.map((r, idx) => (
                                                <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                                                    <td className="px-3 py-2 text-center">
                                                        <input type="checkbox" checked={r.checked} onChange={(e) => handleOldCheck(r.id, e.target.checked)} disabled={isBusy} className="accent-indigo-600 h-4 w-4" />
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.description}</td>
                                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.unit}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{r.quantity}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatIndianCurrency(r.rate)}</td>
                                                    <td className="px-3 py-2">
                                                        <select
                                                            className={`${inputCls} appearance-none py-1.5`}
                                                            value={r.type}
                                                            onChange={(e) => handleOldTypeChange(r.id, e.target.value)}
                                                            disabled={!r.checked || isBusy}
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="Add">Add</option>
                                                            <option value="Substract">Substract</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <input
                                                            type="text" inputMode="decimal"
                                                            className={`${inputCls} py-1.5 text-right`}
                                                            value={r.amendQty}
                                                            onChange={(e) => handleOldQtyChange(r, e.target.value)}
                                                            disabled={!r.checked || !r.type || isBusy}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(oldItemAmount(r))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* ── Section 4: New Items ─────────────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={Plus} title="New Item Description" subtitle="Add brand-new line items to this PO"
                                action={
                                    !showNewItems ? (
                                        <button onClick={handleAddNewItems} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50">
                                            <Plus className="h-3.5 w-3.5" /> Add New Items
                                        </button>
                                    ) : (
                                        <button onClick={handleRemoveNewItems} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-600 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 disabled:opacity-50">
                                            <Trash2 className="h-3.5 w-3.5" /> Remove New Items
                                        </button>
                                    )
                                }
                            />
                            {showNewItems && (
                                <div className="p-6 md:p-8">
                                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                                                    <th className="px-3 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Chk</th>
                                                    <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Description</th>
                                                    <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Unit</th>
                                                    <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Qty</th>
                                                    <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Our Rate</th>
                                                    <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">PRW Rate</th>
                                                    <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Rate</th>
                                                    <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Amount</th>
                                                    <th className="px-3 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {newItems.map((r, idx) => (
                                                    <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                                                        <td className="px-3 py-2 text-center">
                                                            <input type="checkbox" checked={r.checked} onChange={(e) => updateNewItem(r.id, { checked: e.target.checked })} disabled={isBusy} className="accent-indigo-600 h-4 w-4" />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <textarea rows={1} className={`${inputCls} py-1.5`} value={r.description} onChange={(e) => updateNewItem(r.id, { description: e.target.value })} disabled={isBusy} />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="text" className={`${inputCls} py-1.5`} value={r.unit} onChange={(e) => updateNewItem(r.id, { unit: e.target.value })} disabled={isBusy} />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.quantity} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateNewItem(r.id, { quantity: e.target.value })} disabled={isBusy} />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.ourRate} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateNewItem(r.id, { ourRate: e.target.value })} disabled={isBusy} />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.prwRate} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateNewItem(r.id, { prwRate: e.target.value })} disabled={isBusy} />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.rate} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateNewItem(r.id, { rate: e.target.value })} disabled={isBusy} />
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(newItemAmount(r))}</td>
                                                        <td className="px-3 py-2 text-center">
                                                            <button onClick={() => setNewItems((p) => p.length > 1 ? p.filter((x) => x.id !== r.id) : p)} disabled={isBusy} className="p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40">
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button onClick={() => setNewItems((p) => [...p, newItemRow()])} disabled={isBusy} className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 disabled:opacity-50">
                                        <Plus className="h-3.5 w-3.5" /> Add New
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* ── Section 5: Totals ─────────────────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="rounded-xl p-4 border-2 border-emerald-500">
                                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1">Substract Amount</p>
                                    <p className="text-lg font-black text-emerald-600">₹ {formatIndianCurrency(totals.minus)}</p>
                                </div>
                                <div className="rounded-xl p-4 border-2 border-rose-500">
                                    <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider mb-1">Add Amount</p>
                                    <p className="text-lg font-black text-rose-600">₹ {formatIndianCurrency(totals.plus)}</p>
                                </div>
                                <div className={`rounded-xl p-4 border-2 ${totals.total < numOr0(poDetails.TotalValue) ? 'border-rose-500' : 'border-emerald-500'}`}>
                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Amended Total Value</p>
                                    <p className={`text-lg font-black ${totals.total < numOr0(poDetails.TotalValue) ? 'text-rose-600' : 'text-emerald-600'}`}>₹ {formatIndianCurrency(totals.total)}</p>
                                </div>
                            </div>
                        </div>

                        {/* ── Section 6: Terms & Conditions ────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={FileStack} title="Terms & Conditions" subtitle="Existing terms are shown for reference; add at least one new term" />
                            <div className="p-6 md:p-8 space-y-3">
                                {oldTerms.map((t, i) => (
                                    <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
                                        <input type="checkbox" checked disabled className="accent-gray-400 h-4 w-4" />
                                        <span className="text-sm text-gray-600 dark:text-gray-300">{i + 1}. {t.term}</span>
                                    </div>
                                ))}
                                {newTerms.map((t, i) => (
                                    <div key={t.id} className="flex items-center gap-3">
                                        <span className="text-xs font-bold text-gray-400 w-6">{oldTerms.length + i + 1}.</span>
                                        <textarea
                                            rows={1} className={`${inputCls} resize-none`}
                                            value={t.term}
                                            onChange={(e) => { if (!e.target.value.includes('|')) updateNewTerm(t.id, e.target.value); }}
                                            disabled={isBusy}
                                            placeholder="Enter new term…"
                                        />
                                        <button onClick={() => setNewTerms((p) => p.length > 1 ? p.filter((x) => x.id !== t.id) : p)} disabled={isBusy} className="p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                                <button onClick={() => setNewTerms((p) => [...p, newTermRow()])} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 disabled:opacity-50">
                                    <Plus className="h-3.5 w-3.5" /> Add New Term
                                </button>
                            </div>
                        </div>

                        {/* ── Section 7: Uploaded Docs & File ──────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={Paperclip} title="Documents" subtitle="Previously uploaded PO documents, and the final scope-of-work checklist" />
                            <div className="p-6 md:p-8">
                                {uploadedDocsLoading ? (
                                    <div className="flex items-center justify-center py-4 text-gray-400"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>
                                ) : uploadedDocs.length > 0 ? (
                                    <ul className="mb-6 space-y-2">
                                        {uploadedDocs.map((d, i) => (
                                            <li key={i} className="text-sm">
                                                {d.Path ? (
                                                    <a href={buildSPPOAmendUrl(d.Path)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                                        {d.PONO} — {d.POType} (document {i + 1})
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">{d.PONO} — {d.POType}: Document Not Uploaded</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}

                                <Label required={requiresUpload}>Upload Final Scope Of Checklist (PDF){!requiresUpload && ' — optional'}</Label>
                                <input type="file" accept=".pdf,application/pdf" onChange={handleFileChange} disabled={isBusy} className={`${inputCls} py-2`} />
                                {file && <p className="text-xs text-gray-500 mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
                            </div>
                        </div>

                        {showSummary && (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                <SectionHeader icon={CheckCircle} title="Amend Summary" subtitle="Review before submitting" />
                                <div className="p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                    {[
                                        { label: 'SPPO No', value: sppoNo },
                                        { label: 'Amend Date', value: formatDateForAPI(amendDate) },
                                        { label: 'Existing Items Amended', value: oldItems.filter((r) => r.checked).length },
                                        { label: 'New Items', value: showNewItems ? newItems.filter((r) => r.checked).length : 0 },
                                        { label: 'Substract Amount', value: `₹ ${formatIndianCurrency(totals.minus)}` },
                                        { label: 'Add Amount', value: `₹ ${formatIndianCurrency(totals.plus)}` },
                                        { label: 'Amended Total Value', value: `₹ ${formatIndianCurrency(totals.total)}` },
                                        { label: 'Created By', value: userName },
                                    ].map(({ label, value }) => (
                                        <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                            <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-3 pb-6">
                            <button onClick={handleFullReset} disabled={isBusy} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                <RotateCcw className="h-4 w-4" /> Reset
                            </button>
                            <button onClick={handleSubmit} disabled={!canSubmit} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                {isBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> {checking ? 'Validating…' : uploading ? 'Uploading…' : 'Saving…'}</> : <><Send className="h-4 w-4" /> Submit</>}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SPPOAmend;
